import { Auth, browserLocalPersistence, browserSessionPersistence,
	createUserWithEmailAndPassword, getAuth, GoogleAuthProvider,
	inMemoryPersistence, sendEmailVerification, sendPasswordResetEmail,
	setPersistence, signInWithEmailAndPassword, signInWithPopup, signOut, User }
	from 'firebase/auth';

export class FbAuth {
	
	public static async createUserWithEmailAndPassword(email:string, 
			password:string):Promise<User> {
		try {
			let auth = getAuth();
			let credential = await createUserWithEmailAndPassword(auth,
				email, password);
			return credential.user;
		}
		catch (err) {
			throw err;
		}
	}
	
	public static emailVerified():boolean {
		return (FbAuth.getCurrentUser()?.emailVerified)?? false;
	}
	
	/**Attempts to send a verification email to the user. If the email is sent,
	 * returns [true]*/
	public static async sendEmailVerification():Promise<boolean> {
		let user = FbAuth.getCurrentUser();
		if (user != null){
			try {
				await sendEmailVerification(user);
				return true;
			}
			catch (err){
				console.log(err);
				return false;
			}
		}
		return false;
	}
	
	public static async signInWithEmailAndPassword(email:string, 
			password:string):Promise<User> {
		try {
			let auth = getAuth();
			let credential = await signInWithEmailAndPassword(auth,
				email, password);
			return credential.user;
		}
		catch (err){
			throw err;
		}
	}
	
	public static async sendPasswordResetEmail(email:string):Promise<void> {
		try {
			let auth = getAuth();
			let result = await sendPasswordResetEmail(auth, email);
			// console.log(result);
		}
		catch(err){
			throw err;
		}
	}
	
	/**Shows a Google login popup - returns a Promise that resolves to the
	 * logged in user, or null if login doesn't occur.*/
	public static async showLogin():Promise<User> {
		try {
			let provider = new GoogleAuthProvider()
			provider.setCustomParameters({ 'prompt': 'select_account'});
			let auth = getAuth();
			let result = await signInWithPopup(auth, provider);
			
			return result.user;
		}
		catch(err){
			throw err;
		}
	}
	
	/**Logs the user out. Returns a Promise that resolves when operation is
	 * complete.*/
	public static async logout():Promise<void> {
		try {
			let auth = getAuth();
			await signOut(auth);
		}
		catch (err){
			throw err;
		}
	}
	
	/**Sets the persistance of the user login. [persistance] matches a value
	 * from [fb.Persistence]. Info here:
	 * https://firebase.google.com/docs/auth/web/auth-state-persistence
	 * @param persistence a string that is LOCAL, SESSION, or NONE*/
	public static async setAuthPersistence(persistence:string = "LOCAL"
			):Promise<void> {
		try {
			let auth = getAuth();
			switch (persistence){
				case "LOCAL":
					await setPersistence(auth, browserLocalPersistence);
					break;
				case "SESSION":
					await setPersistence(auth, browserSessionPersistence);
					break;
				case "NONE":
					await setPersistence(auth, inMemoryPersistence);
					break;
				default:
					throw 'invalid persistence value: ' + persistence;
			}
		}
		catch(err){
			throw err;
		}
	}
	
	/**gets the currently signed in user*/
	public static getCurrentUser():User {
		return getAuth().currentUser;
	}
	
	public static async reloadUser():Promise<void> {
		let user = FbAuth.getCurrentUser();
		if (user != null){
			try {
				await user.reload();
			}
			catch (err){
				console.log(err);
			}
		}
	}
	
	/**gets the JWT auth token for the current user - returns null if user
	 * not logged in.*/
	public static async getUserToken(forceRefresh:boolean = false)
			:Promise<string> {
		try {
			let user = getAuth().currentUser;
			if (user != null){
				let token:string = await user.getIdToken(forceRefresh);
				return token;
			}
			return null;
		}
		catch(err) {
			throw err;
		}
	}
	
	public static get auth():Auth { return getAuth(); }
}